import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { API_BASE_URL } from '../api-base-url.token';

export interface Skill {
  id: string;
  code: string;
  label: string;
}

export interface JobTitle {
  id: string;
  code: string;
  label: string;
  description?: string;
  color: string;
}

export interface UserNote {
  id: string;
  author_id: string;
  author_name: string;
  date: string;
  text: string;
  created_at: string;
}

@Injectable({
  providedIn: 'root'
})
export class TeamService {
  private http = inject(HttpClient);
  private base = inject(API_BASE_URL);

  getSkills(): Observable<Skill[]> {
    return this.http
      .get<{ skills: Skill[] }>(`${this.base}/api/v1/skills`)
      .pipe(map(r => r.skills));
  }

  createSkill(payload: { code: string; label: string }): Observable<Skill> {
    return this.http.post<Skill>(`${this.base}/api/v1/skills`, payload);
  }

  updateSkill(skillId: string, payload: { code: string; label: string }): Observable<Skill> {
    return this.http.put<Skill>(`${this.base}/api/v1/skills/${skillId}`, payload);
  }

  deleteSkill(skillId: string): Observable<{ title: string; message: string }> {
    return this.http.delete<{ title: string; message: string }>(`${this.base}/api/v1/skills/${skillId}`);
  }

  /** Référentiel quasi-statique : partagé entre tous les appelants (shell, site, gestion-equipes, user-list-modal)
   *  au lieu d'un appel HTTP par composant monté. Invalidé sur create/update/delete. */
  private jobTitles$?: Observable<JobTitle[]>;

  getJobTitles(): Observable<JobTitle[]> {
    if (!this.jobTitles$) {
      this.jobTitles$ = this.http
        .get<{ job_titles: JobTitle[] }>(`${this.base}/api/v1/job-titles`)
        .pipe(map(r => r.job_titles), shareReplay(1));
    }
    return this.jobTitles$;
  }

  createJobTitle(payload: { code: string; label: string; description?: string; color?: string }): Observable<JobTitle> {
    return this.http.post<JobTitle>(`${this.base}/api/v1/job-titles`, payload)
      .pipe(tap(() => this.jobTitles$ = undefined));
  }

  updateJobTitle(jobTitleId: string, payload: { code: string; label: string; description?: string; color: string }): Observable<JobTitle> {
    return this.http.put<JobTitle>(`${this.base}/api/v1/job-titles/${jobTitleId}`, payload)
      .pipe(tap(() => this.jobTitles$ = undefined));
  }

  deleteJobTitle(jobTitleId: string): Observable<{ title: string; message: string }> {
    return this.http.delete<{ title: string; message: string }>(`${this.base}/api/v1/job-titles/${jobTitleId}`)
      .pipe(tap(() => this.jobTitles$ = undefined));
  }

  setUserSkills(userId: string, skillIds: string[]): Observable<{ title: string; message: string }> {
    return this.http.put<{ title: string; message: string }>(
      `${this.base}/api/v1/users/${userId}/skills`,
      { skill_ids: skillIds }
    );
  }

  setUserEstablishments(userId: string, establishmentIds: string[]): Observable<{ title: string; message: string }> {
    return this.http.put<{ title: string; message: string }>(
      `${this.base}/api/v1/users/${userId}/establishments`,
      { establishment_ids: establishmentIds }
    );
  }

  setUserJobTitles(userId: string, jobTitleIds: string[]): Observable<{ title: string; message: string }> {
    return this.http.put<{ title: string; message: string }>(
      `${this.base}/api/v1/users/${userId}/job-titles`,
      { job_title_ids: jobTitleIds }
    );
  }

  getUserNotes(userId: string): Observable<UserNote[]> {
    return this.http
      .get<{ notes: UserNote[] }>(`${this.base}/api/v1/users/${userId}/notes`)
      .pipe(map(r => r.notes));
  }

  createUserNote(userId: string, payload: { date: string; text: string }): Observable<{ note_id: string; title: string; message: string }> {
    return this.http.post<{ note_id: string; title: string; message: string }>(
      `${this.base}/api/v1/users/${userId}/notes`,
      payload
    );
  }
}
